import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { WidgetQueryOptions } from 'app/modules/collection/services/filter.service';
import { Comparison, Dashboard, WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import { ActionItem, ActionPlan, Status } from 'app/providers/storage-api/actionplan.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { getMinDueDate, getStatusDs } from 'app/utils/action-plan.utils';
import { ExtractFormControl } from 'app/utils/validator.utils';
import { WidgetDataSource } from '../../../../models';
import {
  ActionItemEditorComponent,
  ActionItemEditorMode,
  ActionItemModel,
} from '../action-item-editor/action-item-editor.component';
import { ActionItemTableComponent } from '../action-item-table/action-item-table.component';

import { WidgetImpactOptions } from '../widget-impact/widget-impact.component';
import { ActionPlanCreationInput, ActionPlanDialogService } from './action-plan-dialog.service';

interface ActionPlanForm {
  creator: FormControl<string>;
  dueDate: FormControl<Date>;
  status: FormControl<Status>;
  notes: FormControl<string>;
}

export type ActionPlanModel = ExtractFormControl<ActionPlanForm>;

export interface ActionPlanDialogOptions {
  actionPlan?: ActionPlan;
  actionItems: ActionItem[];
  forItem: boolean;
  itemTag: string;
  widgetDataSource: WidgetDataSource;
  dashboard: Dashboard;
  payload: WidgetImpactOptions;
  widgetQueryOptions: WidgetQueryOptions;
  widgetId: string;
  widgetGroup?: WidgetGroup;
  comparison?: Comparison;
}

export type ActionItemState = 'updated' | 'deleted' | 'original';

export interface ActionItemDto extends ActionItem {
  state: ActionItemState;
  statusText: string;
  isNew?: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-dialog',
  imports: [AppSharedModule, ActionItemTableComponent, ActionItemEditorComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [ActionPlanDialogService],
  templateUrl: './action-plan-dialog.component.html',
  styleUrls: ['./action-plan-dialog.component.scss'],
})
export class ActionPlanDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() options!: ActionPlanDialogOptions;

  @Output() close = new EventEmitter<void>();

  isLoading = false;
  isSaving = false;

  form!: FormGroup<ActionPlanForm>;

  statusDs: DropdownObject[] = getStatusDs();
  minDueDate: Date = getMinDueDate();

  actionItemEditorMode: ActionItemEditorMode = 'none';
  isItemEditorValueChanged = false;

  items: ActionItemDto[] = [];
  selectedItem: ActionItemDto;

  constructor(
    private formBuilder: FormBuilder,
    private actionPlanDialogService: ActionPlanDialogService
  ) {
    this.buildForm();
  }

  async ngOnInit(): Promise<void> {
    await this.init();
  }

  async onSave(): Promise<void> {
    const isSuccess = await this.save();

    if (isSuccess) {
      this.close.emit();
    }
  }

  onCancel(): void {
    this.close.emit();
  }

  onItemEditorSave(item: ActionItemModel): void {
    let autoSelectItem: ActionItemDto;
    if (this.actionItemEditorMode === 'create') {
      autoSelectItem = ActionPlanDialogService.createItemDto(
        item,
        this.options.payload.analyticItem,
        this.options.itemTag
      );

      this.items.push(autoSelectItem);
    } else {
      ActionPlanDialogService.updateItemDto(this.selectedItem, item);

      autoSelectItem = this.selectedItem;
    }

    this.resetDialog();

    // to trigger onchanges in item editor
    setTimeout(() => this.onSelectItem(autoSelectItem));
  }

  onItemEditorCancel(): void {
    this.resetDialog(false);
  }

  onItemEditorDelete(): void {
    this.selectedItem.state = 'deleted';

    this.resetDialog();
  }

  onItemEditorValueChange(): void {
    this.isItemEditorValueChanged = true;

    this.form.disable();
  }

  onSelectItem(item: ActionItemDto): void {
    this.selectedItem = item;

    this.setItemEditorTo('edit');
  }

  get isCancelDisabled(): boolean {
    return this.isActionItemEditing || this.isSaving;
  }

  get isSaveDisabled(): boolean {
    return this.form.invalid || this.isActionItemEditing || this.isSaving;
  }

  get isActionItemEditing(): boolean {
    return (
      this.actionItemEditorMode === 'create' ||
      (this.actionItemEditorMode === 'edit' && this.isItemEditorValueChanged)
    );
  }

  get isActionItemTableDisabled(): boolean {
    return this.isActionItemEditing || this.isSaving;
  }

  get hasActionPlan(): boolean {
    return !!this.options.actionPlan;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group(<ActionPlanForm>{
      creator: new FormControl({ value: '', disabled: true }, Validators.required),
      dueDate: new FormControl(new Date(), Validators.required),
      status: new FormControl({ value: Status.NEW, disabled: true }, Validators.required),
      notes: new FormControl(''),
    });
  }

  private patchFormValues(): void {
    if (!this.hasActionPlan) {
      return;
    }

    this.form.patchValue({
      creator: this.options.actionPlan.creator_user_name,
      dueDate: new Date(this.options.actionPlan.due_date),
      status: this.options.actionPlan.status,
      notes: this.options.actionPlan.notes,
    });

    this.form.controls.status.enable();
  }

  private setItemEditorTo(mode: 'create' | 'edit'): void {
    this.actionItemEditorMode = mode;
  }

  private resetDialog(refreshItems = true): void {
    this.actionItemEditorMode = 'none';

    this.isItemEditorValueChanged = false;

    this.form.enable();

    this.selectedItem = undefined;

    if (refreshItems) {
      this.items = [...this.items];
    }
  }

  private async init(): Promise<void> {
    this.isLoading = true;

    await this.setCurrentUserNameToForm();

    this.patchFormValues();

    if (this.options.actionItems.length) {
      this.items = ActionPlanDialogService.mapItemsToDtos(this.options.actionItems);
    }

    if (this.options.forItem) {
      this.setItemEditorTo('create');

      this.form.disable();
    }

    this.isLoading = false;
  }

  private async setCurrentUserNameToForm(): Promise<void> {
    if (this.hasActionPlan) {
      return;
    }

    const currentUserFullName = await this.actionPlanDialogService.getCurrentUserFullName();

    this.form.patchValue({
      creator: currentUserFullName,
    });
  }

  private async save(): Promise<boolean> {
    this.isSaving = true;
    this.form.disable();

    let isSuccess = true;

    const actionPlan = this.form.getRawValue() as ActionPlanModel;

    try {
      if (!this.hasActionPlan) {
        const input = new ActionPlanCreationInput(actionPlan, this.options);

        await this.actionPlanDialogService.createActionPlan(input);
      } else {
        await this.actionPlanDialogService.updateActionPlan(this.options.actionPlan, actionPlan);
      }

      if (this.items.length) {
        await this.actionPlanDialogService.saveActionItems(
          this.options.actionPlan.plan_id,
          this.items
        );
      }
    } catch {
      isSuccess = false;
    }

    this.form.enable();
    this.isSaving = false;

    return isSuccess;
  }
}
